import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router";
import { useLingui } from "@lingui/react";
import { useAuth } from "@clerk/clerk-react";
import { QueryClientContext } from "@tanstack/react-query";
import { AuthProvider } from "./contexts/AuthContext";
import { AccessProvider } from "./contexts/AccessContext";
import { useAccess } from "./hooks/useAccess";
import { appRoutes } from "./routes/appRoutes";
import { StaffNotAuthorizedPage } from "./pages/StaffNotAuthorizedPage";
import { AppSidebar } from "./components/AppSidebar";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Toaster } from "sonner";
import { toastOptions } from "@/lib/toastOptions";
import { BackNavigationProvider } from "./contexts/BackNavigationContext";
import { DunningWorkflowDrawerProvider } from "./contexts/DunningWorkflowDrawerContext";
import { BackButton } from "./components/shared/BackButton";
import { Breadcrumbs } from "./components/layout/Breadcrumbs";
import { RouteErrorBoundary } from "./components/layout/RouteErrorBoundary";
import { CommandPalette } from "./components/layout/CommandPalette";
import { clearRecentPaletteItems } from "./components/layout/commandPaletteRecents";
import { CommandPaletteProvider, useCommandPalette } from "./contexts/CommandPaletteContext";
import { APP_TITLE, formatDocumentTitle, routeTitle } from "./lib/routeTitles";
import { BreadcrumbProvider } from "./contexts/BreadcrumbContext";
import { Search } from "lucide-react";
import { WelcomePage } from "./pages/auth/WelcomePage";
import { SignInPage } from "./pages/auth/SignInPage";
import { useSentryUserContext } from "./hooks/useSentryUserContext";
import { useOrgTimezone } from "./hooks/settings/useOrgTimezone";
import { FeedbackButton } from "./components/FeedbackButton";
import { Trans } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  buildSignInRedirect,
  canUseMePortal,
  clearLastActivity,
  isMePath,
  landsOnMePortal,
} from "./components/me/mePortal";
import { MeBootGate } from "./hooks/me/useMeBootSignOut";
import { useMeActivityRelay } from "./hooks/me/useMeIdleSignOut";
import { resetMeSignOut } from "./hooks/me/useMeSignOut";

// The /me self-service portal (#1768) is its own shell, loaded on demand so the
// admin bundle does not grow for admins who never open it.
const MePortal = React.lazy(() => import("./pages/me/MePortal"));

// Suspense fallback shown while a lazily-imported route chunk loads.
function RouteLoadingFallback() {
  return (
    <div className="flex h-full w-full items-center justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

// The sidebar writes its open/collapsed state to this cookie; reading it back
// keeps a collapsed sidebar collapsed across reloads.
function readSidebarOpen(): boolean {
  if (typeof document === "undefined") return true;
  const match = document.cookie.match(/(?:^|; )sidebar_state=(true|false)/);
  return match ? match[1] === "true" : true;
}

// Keeps document.title in sync with the active route and announces the page
// name to assistive tech via a polite live region (SPA navigations don't emit
// a native page-load announcement).
function RouteAnnouncer() {
  const location = useLocation();
  const { i18n } = useLingui();
  const [announcement, setAnnouncement] = React.useState("");

  // Keyed on the locale, not the i18n instance: the instance never changes,
  // so a language switch would otherwise leave the title in the old language.
  React.useEffect(() => {
    const pageName = routeTitle(location.pathname, i18n) ?? "";
    document.title = formatDocumentTitle(pageName);
    setAnnouncement(pageName || APP_TITLE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, i18n.locale]);

  return (
    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
      {announcement}
    </div>
  );
}

// The command palette trigger. The button carries the shortcut hint so people
// learn it.
function TopBarSearchButton() {
  const { i18n } = useLingui();
  const label = i18n._({ id: "commandPalette.open", message: "Search pages, settings and records" });
  const { open } = useCommandPalette();
  return (
    <Button
      variant="outline"
      size="sm"
      className="h-8 gap-2 text-muted-foreground"
      onClick={open}
      aria-label={label}
      title={label}
    >
      <Search className="h-4 w-4" aria-hidden="true" />
      <span className="hidden md:inline">{i18n._({ id: "commandPalette.trigger", message: "Search" })}</span>
      <kbd className="pointer-events-none hidden select-none rounded border bg-muted px-1.5 font-mono text-xs font-medium text-muted-foreground md:inline">
        {navigator.platform.toUpperCase().includes("MAC") ? "\u2318K" : "Ctrl K"}
      </kbd>
    </Button>
  );
}

// Layout for authenticated users with sidebar navigation
function AppLayout() {
  const { isLoaded } = useAuth();
  
  // Set Sentry user context from Clerk user data
  useSentryUserContext();

  // Load organisations.timezone once and install it for the date helpers in
  // utils/dateUtils (issue #1287). Mounted here because every screen renders
  // times through those helpers; until it resolves they use the documented
  // Europe/Zurich default rather than the browser's zone or UTC.
  useOrgTimezone();

  // Clerk sign-out is session-wide: activity here keeps a live /me tab of the
  // same browser from idling out this session (#1768).
  useMeActivityRelay();

  if (!isLoaded) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            <Trans id="auth.loading">Signing you in...</Trans>
          </p>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider defaultOpen={readSidebarOpen()}>
      <BackNavigationProvider>
      <BreadcrumbProvider>
      <CommandPaletteProvider>
      <DunningWorkflowDrawerProvider>
      {/* Skip-to-content link: first focusable element in the tab order,
          visually hidden until focused. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-md focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <Trans id="a11y.skipToContent">Skip to main content</Trans>
      </a>
      <RouteAnnouncer />
      <AppSidebar />
      <SidebarInset className="max-w-full overflow-x-clip">
        <div className="w-full bg-background h-full flex flex-col">
          {/* One slim sticky bar: where am I, how do I get back, how do I jump anywhere. */}
          <header className="sticky top-0 z-30 flex h-12 shrink-0 items-center gap-2 border-b bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:rounded-t-xl print:hidden">
            <SidebarTrigger className="-ml-1" />
            <div className="h-4 w-px bg-border mx-1" />
            <BackButton />
            <Breadcrumbs className="flex-1" />
            <TopBarSearchButton />
          </header>
          <main
            id="main-content"
            tabIndex={-1}
            className="flex-1 px-3 sm:px-4 md:px-6 py-4 focus:outline-none"
          >
            <RouteErrorBoundary>
              <React.Suspense fallback={<RouteLoadingFallback />}>
                <Routes>
                  {appRoutes}
                </Routes>
              </React.Suspense>
            </RouteErrorBoundary>
          </main>
        </div>
      </SidebarInset>
      <Toaster position="top-right" richColors closeButton className="print:hidden" toastOptions={toastOptions} />
      <CommandPalette />
      <FeedbackButton />
      </DunningWorkflowDrawerProvider>
      </CommandPaletteProvider>
      </BreadcrumbProvider>
      </BackNavigationProvider>
    </SidebarProvider>
  );
}

// This component will wrap protected routes
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            <Trans id="common.loading">Loading...</Trans>
          </p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Mounts the portal for every /me path. Its routes are relative to /me.
function MePortalRoute() {
  return (
    <React.Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        <Route path="/me/*" element={<MePortal />} />
      </Routes>
    </React.Suspense>
  );
}

// Decides admin / employee access once AccessProvider has resolved it. On /me
// the portal shell replaces AppLayout; an app-only employee lands there.
function AccessGate() {
  const access = useAccess();
  const location = useLocation();
  const onMe = isMePath(location.pathname);

  // An admin opens /me with ZERO access requests, as everywhere else.
  if (onMe && access.isAdmin) {
    return (
      <ProtectedRoute>
        <MePortalRoute />
      </ProtectedRoute>
    );
  }

  if (access.isAdmin) {
    return (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    );
  }

  if (access.isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <div className="text-center" role="status">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            <Trans id="access.loading">Loading your access...</Trans>
          </p>
        </div>
      </div>
    );
  }

  // `/access/me` admits any module, so a 403 means the role holds none.
  if (access.errorStatus === 403) {
    return <StaffNotAuthorizedPage reason="noWebAccess" />;
  }

  if (access.isError) {
    return <AccessLoadError onRetry={access.refetch} isRetrying={access.isFetching} />;
  }

  // The portal needs a module the backend's employeeSelfServiceGuard admits; a
  // web employee without one goes home rather than to a page of 403 states.
  if (onMe) {
    if (canUseMePortal(access)) {
      return (
        <ProtectedRoute>
          <MePortalRoute />
        </ProtectedRoute>
      );
    }
    return access.webAccess ? <Navigate to="/" replace /> : <StaffNotAuthorizedPage reason="noWebAccess" />;
  }

  if (access.webAccess) {
    return (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    );
  }

  // An app-only employee (self-service / mobile-employee) uses the portal.
  if (landsOnMePortal(access)) {
    return <Navigate to="/me" replace />;
  }

  return <StaffNotAuthorizedPage reason="noWebAccess" />;
}

// A signed-out deep link goes to sign-in and carries the path through it.
function SignedOutRedirect() {
  const location = useLocation();
  return <Navigate to={buildSignInRedirect(location.pathname, location.search)} replace />;
}

// The signed-out tree. A /me activity marker left by an expired Clerk session
// is cleared here, so a later sign-in in this tab never trips the boot check.
// The query cache is cleared too: a sign-out in ANOTHER tab reaches this one
// only through Clerk's cross-tab sync (isSignedIn flips), never through the
// /me sign-out that clears the cache first (#1768). Read through the context,
// not useQueryClient: the signed-out tree must also render without a provider.
function SignedOutRoutes() {
  const queryClient = React.useContext(QueryClientContext);

  React.useEffect(() => {
    queryClient?.clear();
    clearLastActivity();
    clearRecentPaletteItems();
    resetMeSignOut();
  }, [queryClient]);

  return (
    <Routes>
      <Route path="/sign-in/*" element={<SignInPage />} />
      <Route path="/" element={<WelcomePage />} />
      <Route path="*" element={<SignedOutRedirect />} />
    </Routes>
  );
}

// A failed access request is not a lock-out: the user may retry.
function AccessLoadError({ onRetry, isRetrying }: { onRetry: () => void; isRetrying: boolean }) {
  const headingRef = React.useRef<HTMLHeadingElement>(null);

  React.useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="h-screen w-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-2xl font-semibold leading-tight tracking-tight outline-none"
          >
            <Trans id="access.loadError.title">Your access could not be loaded</Trans>
          </h1>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            <Trans id="access.loadError.message">Check your connection and try again.</Trans>
          </p>
          <Button className="w-full" onClick={onRetry} disabled={isRetrying}>
            <Trans id="access.loadError.retry">Try again</Trans>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function App() {
  const { isLoaded, isSignedIn, orgId } = useAuth();

  try {
    if (!isLoaded) {
      return (
        <div className="h-screen w-screen flex items-center justify-center bg-background">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">
              <Trans id="auth.loading">Signing you in...</Trans>
            </p>
          </div>
        </div>
      );
    }

    if (!isSignedIn) {
      return <SignedOutRoutes />;
    }

    // Web-admin root gate (issue #1255, four-way since #1670). orgRole is
    // undefined both while Clerk loads and when the session carries no active
    // organization, and neither state means "this user is a caregiver" (#1255
    // adversarial gap A2). The loading branch is handled above; the rest:
    //
    //   1. no active organization -> lock-out `noActiveOrganization`;
    //   2. admin -> the app, with ZERO access requests;
    //   3. employee -> exactly one `GET /api/v1/access/me` before any route
    //      mounts; a spinner while it loads (never the lock-out), then the app
    //      when the role holds a web module, the /me portal when it holds only
    //      `self-service` / `mobile-employee`, else lock-out `noWebAccess`.
    //
    // /me (#1768) is checked first inside AccessGate: an admin gets the portal
    // at once; an employee gets it once access resolved and the role holds a
    // portal module (`self-service`, `mobile-employee`,
    // `time-tracking-workforce`), else `/` (web access) or `noWebAccess`.
    // MeBootGate sits OUTSIDE AuthProvider/AccessProvider: a /me session idle
    // for 30 minutes is signed out before any request runs.
    //
    // The server module gates are the security boundary; this gate is UX.
    if (!orgId) {
      return <StaffNotAuthorizedPage reason="noActiveOrganization" />;
    }

    return (
      <MeBootGate>
        <AuthProvider>
          <AccessProvider>
            <AccessGate />
          </AccessProvider>
        </AuthProvider>
      </MeBootGate>
    );
  } catch (error) {
    console.error("App error:", error);
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">
            <Trans id="app.crash.title">Something went wrong</Trans>
          </h1>
          <p className="text-muted-foreground mb-4">
            <Trans id="app.crash.body">Reload the page. If it happens again, tell the office.</Trans>
          </p>
          <Button onClick={() => window.location.reload()}>
            <Trans id="app.crash.reload">Reload page</Trans>
          </Button>
        </div>
      </div>
    );
  }
}

export default App;
